import { useState } from 'react';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const milestones = [
  { year: '2018', title: '社区成立', desc: '黑料社区正式上线运营，初始团队仅有5人，怀揣着打造真实内容平台的愿景出发。' },
  { year: '2019', title: '用户突破10万', desc: '凭借优质内容和用户口碑，注册用户数量迅速突破10万大关。' },
  { year: '2020', title: '内容体系完善', desc: '建立了六大内容分类板块，形成了完整的内容审核与发布体系。' },
  { year: '2021', title: '月活突破30万', desc: '平台月活跃用户突破30万，日均内容发布量超过200篇。' },
  { year: '2022', title: '技术升级', desc: '全面升级平台技术架构，引入AI内容审核系统，提升内容质量与安全性。' },
  { year: '2023', title: '社区生态成熟', desc: '形成了健康活跃的社区生态，用户自产内容占比超过60%。' },
  { year: '2024', title: '用户突破百万', desc: '累计注册用户突破100万，成为国内领先的真实内容分享平台。' },
  { year: '2025', title: '品牌升级', desc: '完成品牌全面升级，优化用户体验，拓展多元化内容服务。' },
];

const testimonials = [
  {
    name: '张先生',
    role: '资深用户 · 3年',
    content: '在黑料社区泡了三年了，这里的内容质量一直很稳定。不像有些平台到处都是标题党，这里的深度调查文章真的能让你了解事件的全貌。',
  },
  {
    name: '李女士',
    role: '活跃投稿者',
    content: '作为经常投稿的用户，我很欣赏社区的审核机制。虽然审核需要时间，但正是这种严谨保证了平台内容的可信度。我的几篇爆料文章都得到了很好的传播。',
  },
  {
    name: '王同学',
    role: '大学生用户',
    content: '同学推荐我来黑料社区看行业揭秘栏目的，没想到一看就停不下来了。特别是那些行业揭秘文章，对我这个即将毕业的学生来说太有价值了。',
  },
  {
    name: '赵先生',
    role: '媒体从业者',
    content: '作为一名媒体人，我觉得黑料社区是难得的优质信息来源。很多深度的社会调查报道，其专业程度不亚于传统媒体的调查报道。',
  },
];

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormState('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    setFormState('submitting');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9qsd86e3oq4jqumtq1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormState('error');
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormState('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormState('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黑料社区',
    url: 'https://heiliaoshequ.com',
    description: '真实内容社区平台，汇聚社会热点、行业揭秘、网友爆料等多元话题。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@heiliaoshequ.com',
    },
  };

  return (
    <>
      <SEO
        title="关于我们 - 黑料社区 | 品牌介绍与联系方式"
        description="了解黑料社区的品牌故事与发展历程。我们致力于打造真实内容社区平台，提供品牌介绍、用户口碑及联系方式等信息。"
        keywords="黑料社区,关于我们,品牌介绍,联系方式,社区介绍,用户口碑"
        canonicalUrl="https://heiliaoshequ.com/about"
      />
      <SchemaOrg type="Organization" data={orgSchema} />

      <main>
        <Navbar />

        <div className="pt-24 md:pt-28 pb-14 md:pb-20">
          <div className="container-custom">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            {/* Brand Intro */}
            <section className="max-w-[860px] mx-auto">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-6">
                关于黑料社区
              </h1>
              <div className="prose-custom text-foreground-700 leading-relaxed space-y-4">
                <p>
                  黑料社区成立于2018年，是一个专注于真实内容分享与社区讨论的在线平台。我们致力于打造一个开放、透明、安全的信息交流环境，让每一个真实的声音都能被听见，让每一个值得关注的真相都能被看见。
                </p>
                <p>
                  经过多年的发展，黑料社区已经形成了以<strong>最新爆料、社会热点、娱乐八卦、行业揭秘、深度调查、网友投稿</strong>六大板块为核心的内容体系。平台汇聚了来自全国各地的优质内容创作者和活跃用户，日均内容发布量超过200篇，月活跃用户突破50万。
                </p>
                <p>
                  我们始终坚持以用户为中心的发展理念，通过严格的内容审核机制、完善的用户反馈体系和持续的技术创新，为用户提供优质、安全的内容消费体验。在信息碎片化、真假难辨的互联网时代，黑料社区始终坚守"真实"这一核心价值。
                </p>
              </div>
            </section>

            {/* Stats */}
            <section className="mt-14 md:mt-20 max-w-[860px] mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: '100万+', label: '注册用户' },
                  { value: '50万+', label: '月活跃用户' },
                  { value: '1000+', label: '独家内容' },
                  { value: '6', label: '内容板块' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 text-center">
                    <div className="text-xl md:text-2xl font-heading font-bold text-primary-600">{stat.value}</div>
                    <div className="mt-1 text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Milestones */}
            <section className="mt-14 md:mt-20 max-w-[860px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block"></div>
                <div className="flex flex-col gap-6">
                  {milestones.map((item, index) => (
                    <div key={item.year} className={`flex flex-col md:flex-row gap-4 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="md:w-1/2 flex md:justify-end">
                        <div className={`bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 ${index % 2 === 0 ? '' : 'md:text-right'}`}>
                          <span className="text-xs font-bold text-primary-600 bg-primary-100 px-2 py-0.5 rounded">{item.year}</span>
                          <h3 className="mt-2 font-heading text-base font-semibold text-foreground-900">{item.title}</h3>
                          <p className="mt-1 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                      <div className="hidden md:flex items-center justify-center flex-shrink-0">
                        <div className="w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50"></div>
                      </div>
                      <div className="md:w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Testimonials */}
            <section className="mt-14 md:mt-20 max-w-[860px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {testimonials.map((item) => (
                  <div key={item.name} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                    <div className="flex items-center gap-1 text-accent-500 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">&ldquo;{item.content}&rdquo;</p>
                    <div className="mt-4 flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-sm">
                        {item.name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800">{item.name}</div>
                        <div className="text-xs text-foreground-500">{item.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section id="contact" className="mt-14 md:mt-20 max-w-[860px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
                联系我们
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8 max-w-xl mx-auto">
                <form onSubmit={handleSubmit} data-readdy-form="true">
                  <div className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入留言主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                      <p className="mt-1 text-xs text-foreground-400">最多500个字符</p>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="antispam-field"
                    />

                    {formState === 'success' && (
                      <div className="p-3 bg-secondary-100 text-secondary-700 rounded-md text-sm">
                        <i className="ri-check-line mr-1"></i> {formMessage}
                      </div>
                    )}
                    {formState === 'error' && (
                      <div className="p-3 bg-accent-100 text-accent-700 rounded-md text-sm">
                        <i className="ri-error-warning-line mr-1"></i> {formMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formState === 'submitting'}
                      className="w-full py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {formState === 'submitting' ? '提交中...' : '提交留言'}
                    </button>
                  </div>
                </form>
              </div>
            </section>
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
}